import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import moment from "moment";

/* ================= CONSTANT ================= */

// BUỔI HỌC
const SHIFT_COLUMNS = ["Có phép", "Không phép", "Đi học"];
const SHIFT_COL_SPAN = SHIFT_COLUMNS.length;

// ĐIỂM DANH ĂN
const EAT_COLUMNS = [
    "Ăn sáng",
    "Phụ sáng",
    "Ăn trưa",
    "Ăn chiều",
    "Phụ chiều",
    "Ăn tối",
];
const EAT_COL_SPAN = EAT_COLUMNS.length;

const COLORS = {
    YELLOW: "f8eb00",
    GREEN: "95cb4a",
    BLUE: "4F81BD",
    LIGHT_GRAY: "E7E6E6",
    RED_TEXT: "FFFF0000",

    // ĂN
    EAT_HEADER: "9BC2E6",
    EAT_SUB_HEADER: "BDD7EE",
    EAT_CELL: "E2EFDA",
};

/* ================= HELPER ================= */

function fill(cell, color) {
    cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: color },
    };
}

function bold(cell) {
    cell.font = { ...cell.font, bold: true };
}

/* ================= MAIN ================= */

export async function exportAttendanceSummaryV3MonthExcel(input, type) {
    const raw = input?.data ?? input;
    const dataList = Array.isArray(raw) ? raw : [raw];

    const workbook = new ExcelJS.Workbook();

    dataList.forEach((data) => {
        const {
            schoolName,
            teamName,
            exelDate,
            shifts = [],
            attendanceRecordExelResponse = [],
        } = data;

        const sheet = workbook.addWorksheet(teamName || "Lớp");

        /* ================= HEADER ================= */

        sheet.mergeCells("A1", "Z1");
        sheet.getCell("A1").value = "BẢNG THỐNG KÊ ĐIỂM DANH LỚP";
        sheet.getCell("A1").font = { bold: true, size: 16, color: { argb: COLORS.RED_TEXT } };

        sheet.mergeCells("A2", "Z2");
        sheet.getCell("A2").value = `Trường: ${schoolName}`;
        bold(sheet.getCell("A2"));

        sheet.mergeCells("A3", "Z3");
        sheet.getCell("A3").value =
            type === "TEACHER" ? `Phòng ban: ${teamName}` : `Lớp: ${teamName}`;
        bold(sheet.getCell("A3"));

        sheet.mergeCells("A4", "Z4");
        sheet.getCell("A4").value = `Thời gian: ${exelDate}`;
        bold(sheet.getCell("A4"));

        /* ================= TABLE HEADER ================= */

        const startRow = 6;

        sheet.mergeCells(startRow, 1, startRow + 2, 1);
        sheet.mergeCells(startRow, 2, startRow + 2, 2);

        sheet.getCell(startRow, 1).value = "STT";
        sheet.getCell(startRow, 2).value = "Họ tên";
        bold(sheet.getCell(startRow, 1));
        bold(sheet.getCell(startRow, 2));
        fill(sheet.getCell(startRow, 1), COLORS.YELLOW);
        fill(sheet.getCell(startRow, 2), COLORS.YELLOW);

        let colIndex = 3;

        /* ===== BUỔI HỌC ===== */
        sheet.mergeCells(
            startRow,
            colIndex,
            startRow,
            colIndex + shifts.length * SHIFT_COL_SPAN - 1
        );
        sheet.getCell(startRow, colIndex).value = "BUỔI HỌC";
        bold(sheet.getCell(startRow, colIndex));
        fill(sheet.getCell(startRow, colIndex), COLORS.BLUE);

        shifts.forEach((shift) => {
            sheet.mergeCells(
                startRow + 1,
                colIndex,
                startRow + 1,
                colIndex + SHIFT_COL_SPAN - 1
            );

            sheet.getCell(startRow + 1, colIndex).value =
                `${shift.name.trim()} (${shift.startTime} - ${shift.endTime})`;
            bold(sheet.getCell(startRow + 1, colIndex));
            fill(sheet.getCell(startRow + 1, colIndex), COLORS.YELLOW);

            SHIFT_COLUMNS.forEach((t, i) => {
                const cell = sheet.getCell(startRow + 2, colIndex + i);
                cell.value = t;
                bold(cell);
                fill(cell, COLORS.YELLOW);
            });

            colIndex += SHIFT_COL_SPAN;
        });

        /* ===== ĐIỂM DANH ĂN ===== */
        const eatStartCol = colIndex;

        sheet.mergeCells(
            startRow,
            eatStartCol,
            startRow,
            eatStartCol + EAT_COL_SPAN - 1
        );
        sheet.getCell(startRow, eatStartCol).value = "ĐIỂM DANH ĂN";
        bold(sheet.getCell(startRow, eatStartCol));
        fill(sheet.getCell(startRow, eatStartCol), COLORS.EAT_HEADER);

        sheet.mergeCells(
            startRow + 1,
            eatStartCol,
            startRow + 1,
            eatStartCol + EAT_COL_SPAN - 1
        );
        sheet.getCell(startRow + 1, eatStartCol).value = "Bữa ăn trong ngày";
        bold(sheet.getCell(startRow + 1, eatStartCol));
        fill(sheet.getCell(startRow + 1, eatStartCol), COLORS.EAT_SUB_HEADER);

        EAT_COLUMNS.forEach((t, i) => {
            const cell = sheet.getCell(startRow + 2, eatStartCol + i);
            cell.value = t;
            bold(cell);
            fill(cell, COLORS.EAT_SUB_HEADER);
        });

        /* ================= DATA ================= */

        const summaryByShift = {};
        shifts.forEach((s) => {
            summaryByShift[s.id] = { present: 0, excused: 0, unexcused: 0 };
        });

        const eatSummary = {
            breakfast: 0,
            secondBreakfast: 0,
            lunch: 0,
            afternoon: 0,
            secondAfternoon: 0,
            dinner: 0,
        };

        let rowIndex = startRow + 3;

        attendanceRecordExelResponse.forEach((student, index) => {
            sheet.getCell(rowIndex, 1).value = index + 1;
            sheet.getCell(rowIndex, 2).value = student.targetName;

            fill(sheet.getCell(rowIndex, 1), COLORS.LIGHT_GRAY);
            fill(sheet.getCell(rowIndex, 2), COLORS.LIGHT_GRAY);

            let c = 3;

            shifts.forEach((shift) => {
                const records =
                    student.attendanceRecordList?.filter(
                        (r) => Number(r.shiftId) === Number(shift.id)
                    ) || [];

                const total = records.reduce(
                    (acc, r) => {
                        acc.present += r.presentTotal || 0;
                        acc.excused += r.excusedAbsentTotal || 0;
                        acc.unexcused += r.unexcusedAbsentTotal || 0;
                        return acc;
                    },
                    { present: 0, excused: 0, unexcused: 0 }
                );

                fill(sheet.getCell(rowIndex, c), COLORS.GREEN);
                fill(sheet.getCell(rowIndex, c + 1), COLORS.GREEN);
                fill(sheet.getCell(rowIndex, c + 2), COLORS.GREEN);

                sheet.getCell(rowIndex, c).value = total.excused;
                sheet.getCell(rowIndex, c + 1).value = total.unexcused;
                sheet.getCell(rowIndex, c + 2).value = total.present;

                summaryByShift[shift.id].excused += total.excused;
                summaryByShift[shift.id].unexcused += total.unexcused;
                summaryByShift[shift.id].present += total.present;

                c += SHIFT_COL_SPAN;
            });

            const eat = student.countAttendanceEat || {};

            [
                eat.breakfast,
                eat.secondBreakfast,
                eat.lunch,
                eat.afternoon,
                eat.secondAfternoon,
                eat.dinner,
            ].forEach((v, i) => {
                const cell = sheet.getCell(rowIndex, eatStartCol + i);
                cell.value = v || 0;
                fill(cell, COLORS.EAT_CELL);
            });

            eatSummary.breakfast += eat.breakfast || 0;
            eatSummary.secondBreakfast += eat.secondBreakfast || 0;
            eatSummary.lunch += eat.lunch || 0;
            eatSummary.afternoon += eat.afternoon || 0;
            eatSummary.secondAfternoon += eat.secondAfternoon || 0;
            eatSummary.dinner += eat.dinner || 0;

            rowIndex++;
        });

        /* ================= TOTAL ROW ================= */

        sheet.mergeCells(rowIndex, 1, rowIndex, 2);
        const totalCell = sheet.getCell(rowIndex, 1);
        totalCell.value = "TỔNG";
        bold(totalCell);
        fill(totalCell, COLORS.YELLOW);
        fill(sheet.getCell(rowIndex, 2), COLORS.YELLOW);

        let cTotal = 3;
        shifts.forEach((shift) => {
            const sum = summaryByShift[shift.id];

            fill(sheet.getCell(rowIndex, cTotal), COLORS.BLUE);
            fill(sheet.getCell(rowIndex, cTotal + 1), COLORS.BLUE);
            fill(sheet.getCell(rowIndex, cTotal + 2), COLORS.BLUE);

            bold(sheet.getCell(rowIndex, cTotal));
            bold(sheet.getCell(rowIndex, cTotal + 1));
            bold(sheet.getCell(rowIndex, cTotal + 2));

            sheet.getCell(rowIndex, cTotal).value = sum.excused;
            sheet.getCell(rowIndex, cTotal + 1).value = sum.unexcused;
            sheet.getCell(rowIndex, cTotal + 2).value = sum.present;

            cTotal += SHIFT_COL_SPAN;
        });

        [
            eatSummary.breakfast,
            eatSummary.secondBreakfast,
            eatSummary.lunch,
            eatSummary.afternoon,
            eatSummary.secondAfternoon,
            eatSummary.dinner,
        ].forEach((v, i) => {
            const cell = sheet.getCell(rowIndex, eatStartCol + i);
            cell.value = v;
            fill(cell, COLORS.EAT_HEADER); // ✅ CHỈ KHU ĂN
            bold(cell);
        });

        /* ================= BORDER + WIDTH ================= */

        const tableEndCol =
            2 + shifts.length * SHIFT_COL_SPAN + EAT_COL_SPAN;

        for (let r = startRow; r <= rowIndex; r++) {
            for (let c = 1; c <= tableEndCol; c++) {
                const cell = sheet.getCell(r, c);
                cell.border = {
                    top: { style: "thin" },
                    left: { style: "thin" },
                    bottom: { style: "thin" },
                    right: { style: "thin" },
                };
                cell.alignment = {
                    vertical: "middle",
                    horizontal: "center",
                    wrapText: true,
                };
            }
        }
        const dataStartRow = startRow + 3;

        for (let r = dataStartRow; r < rowIndex; r++) {
            sheet.getCell(r, 2).alignment = {
                vertical: "middle",
                horizontal: "left",
                wrapText: true,
                indent: 1,
            };
        }

        sheet.getColumn(1).width = 6;
        sheet.getColumn(2).width = 28;

        let w = 3;
        shifts.forEach(() => {
            sheet.getColumn(w).width = 12;
            sheet.getColumn(w + 1).width = 14;
            sheet.getColumn(w + 2).width = 10;
            w += SHIFT_COL_SPAN;
        });

        for (let i = 0; i < EAT_COL_SPAN; i++) {
            sheet.getColumn(eatStartCol + i).width = 12;
        }

        sheet.pageSetup.orientation = "landscape";
        sheet.pageSetup.paperSize = 9; // A4

        sheet.pageSetup.fitToPage = true;
        sheet.pageSetup.fitToWidth = 1;
        sheet.pageSetup.fitToHeight = 0;

        sheet.pageSetup.printTitlesRow = `${startRow}:${startRow + 2}`;

        sheet.views = [
            {
                state: "frozen",
                ySplit: startRow + 2,
            },
        ];

    });

    const buffer = await workbook.xlsx.writeBuffer();
    const fileDate = dataList[0]?.exelDate || "KHONG_XAC_DINH";
    let myDate = moment().format("DD-MM-YYYY hh:mm:ss");
    if (type === "STUDENT") {
        saveAs(
            new Blob([buffer]),
            `BANG_THONG_KE_DIEM_DANH_LOP_${fileDate}_${myDate}.xlsx`
        );
    } else if (type === "TEACHER") {
        saveAs(
            new Blob([buffer]),
            `BANG_THONG_KE_CHAM_CONG_NHAN_VIEN_${fileDate}_${myDate}.xlsx`
        );
    }
}
